import { initializeApp } from "firebase/app";
// ❌ REMOVED: import { getMessaging, getToken, onMessage } from "firebase/messaging";
// Now dynamically imported inside functions only — fixes Instagram iOS blank screen
import { getFirestore, collection, doc, getDoc, setDoc, updateDoc, deleteDoc, getDocs, serverTimestamp } from "firebase/firestore";
import { getAuth, FacebookAuthProvider, TwitterAuthProvider ,  OAuthProvider } from "firebase/auth"; 
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDatabase } from "firebase/database";

const firebaseConfig = {
  apiKey: "AIzaSyAeY6IbW_mftJJNSroTEwTtSWT4XhJjXj8",
  authDomain: "houseofstox-bc60c.firebaseapp.com",
  projectId: "houseofstox-bc60c",
  storageBucket: "houseofstox-bc60c.firebasestorage.app",
  messagingSenderId: "827654381844",
  appId: "1:827654381844:web:4a2c62c9c9478412216201",
  measurementId: "G-FK7CFX5410"
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
// ❌ REMOVED: const messaging = getMessaging(app);
// ^^^ THIS WAS THE CRASH LINE on Instagram iOS (WKWebView has no Notification API)
const db = getFirestore(app);
const functions = getFunctions(app);
const realdb = getDatabase(app);

// ✅ KEPT: Facebook and Twitter providers — completely unchanged
export const provider = new FacebookAuthProvider();
export const twitterProvider = new TwitterAuthProvider();
export const appleProvider = new OAuthProvider("apple.com");

// ============================================
// ✅ ADDED: Lazy Messaging Helpers
// Instagram iOS (WKWebView) crashes when getMessaging() runs at module load
// because it has no Notification API. These helpers only initialize messaging
// when it is actually needed, and only if the browser supports it.
// ============================================

const isMessagingSupported = () => {
  try {
    return (
      typeof window !== "undefined" &&
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window
    );
  } catch {
    return false;
  }
};

let _messaging = null;

const getMessagingInstance = async () => {
  if (_messaging) return _messaging;
  if (!isMessagingSupported()) return null;
  try {
    const { getMessaging } = await import("firebase/messaging");
    _messaging = getMessaging(app);
    return _messaging;
  } catch {
    return null;
  }
};

// ============================================

// export const requestFcmToken = async () => {
//   try {
//     const token = await getToken(messaging, {
//       vapidKey:
//         "BMhaCOXYLM7kOLat7BQNZtyU5noZqbgGqRLzKxbglkCY-mUveE2feitHtizwQT8dfF-hvm23hPAb9hVSo7W2K8g",
//     });
//     if (token) {
//       return token;
//     } else {
//       console.warn("No registration token available. Request permission.");
//       return null;
//     }
//   } catch (err) {
//     console.error("An error occurred while retrieving token.", err);
//     return null;
//   }
// };

export const requestFcmToken = async () => {
  try {
    // ✅ CHANGED: now uses isMessagingSupported() instead of manual Notification check
    if (!isMessagingSupported()) return null;

    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
      || await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    await navigator.serviceWorker.ready;

    const permission = Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;

    if (permission !== 'granted') return null;

    // ✅ CHANGED: was getToken(messaging, {...})
    // Now gets lazy instance and dynamically imports getToken
    const messagingInstance = await getMessagingInstance();
    if (!messagingInstance) return null;

    const { getToken } = await import("firebase/messaging");
    const token = await getToken(messagingInstance, {
      vapidKey: "BMhaCOXYLM7kOLat7BQNZtyU5noZqbgGqRLzKxbglkCY-mUveE2feitHtizwQT8dfF-hvm23hPAb9hVSo7W2K8g",
      serviceWorkerRegistration: registration
    });

    return token || null;
  } catch (err) {
    // console.error('FCM token error:', err.message);
    return null;
  }
};

// ✅ CHANGED: was sync, now async — uses lazy instance instead of module-level messaging
export const onMessageListener = async (callback) => {
  try {
    const messagingInstance = await getMessagingInstance();
    if (!messagingInstance) return () => {};
    const { onMessage } = await import("firebase/messaging");
    return onMessage(messagingInstance, (payload) => {
      // console.log("Foreground message received:", payload);
      callback(payload);
    });
  } catch {
    return () => {};
  }
};

// Function: Subscribe to topic via Firebase Cloud Function
export const subscribeToTopic = async (token, topic) => {
  const subscribeFn = httpsCallable(functions, "subscribeToTopic");
  try {
    const result = await subscribeFn({ token, topic });
    // console.log("Subscribed:", result.data);
    return result.data;
  } catch (err) {
    // console.error("Error subscribing to topic:", err);
  }
};

// ============================================
// Firestore Collection Management Functions
// ============================================

/**
 * Sanitize token ID to be used as Firestore document ID
 * Firestore document IDs cannot contain: . / \ [ ]
 */
export const sanitizeTokenId = (token) => {
  return token.replace(/[.#$/[\]]/g, "_");
};

/**
 * Initialize or get user notification document
 * Creates: notifications/{userId} document with createdAt field
 */
export const initializeUserNotificationDoc = async (userId) => {
  try {
    const userDocRef = doc(db, "notifications", String(userId));
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      await setDoc(userDocRef, {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      //   console.log(`Created notification document for user ${userId}`);
    }

    return userDocRef;
  } catch (error) {
    // console.error("Error initializing user notification doc:", error);
    throw error;
  }
};

/**
 * Save FCM token to Firestore
 * Structure: notifications/{userId}/fcm_tokens/{tokenId}
 * Fields: token, createdAt, updatedAt
 */
export const saveFcmTokenToFirestore = async (userId, token) => {
  try {
    if (!userId || !token) {
      throw new Error("UserId and token are required");
    }

    // Initialize user notification document if it doesn't exist
    await initializeUserNotificationDoc(userId);

    // Sanitize token ID for use as document ID
    const safeTokenId = sanitizeTokenId(token);

    // Reference to the fcm_tokens subcollection
    const tokensColRef = collection(
      db,
      "notifications",
      String(userId),
      "fcm_tokens"
    );

    // Reference to the specific token document
    const tokenDocRef = doc(tokensColRef, safeTokenId);

    // Save or update the token document
    await setDoc(
      tokenDocRef,
      {
        token: token,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // console.log(`Saved FCM token for user ${userId}`);
    return tokenDocRef;
  } catch (error) {
    // console.error("Error saving FCM token to Firestore:", error);
    throw error;
  }
};

/**
 * Get all FCM tokens for a user
 * Returns array of token documents from notifications/{userId}/fcm_tokens
 */
export const getUserFcmTokens = async (userId) => {
  try {
    const tokensColRef = collection(
      db,
      "notifications",
      String(userId),
      "fcm_tokens"
    );

    const querySnapshot = await getDocs(tokensColRef);
    const tokens = [];

    querySnapshot.forEach((doc) => {
      tokens.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return tokens;
  } catch (error) {
    // console.error("Error getting user FCM tokens:", error);
    throw error;
  }
};

/**
 * Delete a specific FCM token from Firestore
 */
export const deleteFcmToken = async (userId, token) => {
  try {
    const safeTokenId = sanitizeTokenId(token);
    const tokenDocRef = doc(
      db,
      "notifications",
      String(userId),
      "fcm_tokens",
      safeTokenId
    );

    await deleteDoc(tokenDocRef);
    // console.log(`Deleted FCM token for user ${userId}`);
    return true;
  } catch (error) {
    // console.error("Error deleting FCM token:", error);
    throw error;
  }
};

/**
 * Complete function to request FCM token and save to Firestore
 * This combines token request with Firestore save
 */
export const requestAndSaveFcmToken = async (userId) => {
  try {
    // ✅ CHANGED: now uses isMessagingSupported() instead of manual Notification check
    if (!isMessagingSupported()) return null;

    // Get service worker registration
    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
      || await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    await navigator.serviceWorker.ready;

    // Request permission
    const permission = Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;

    if (permission !== 'granted') {
      //   console.warn("Notification permission not granted");
      return null;
    }

    // ✅ CHANGED: was getToken(messaging, {...})
    // Now gets lazy instance and dynamically imports getToken
    const messagingInstance = await getMessagingInstance();
    if (!messagingInstance) return null;

    const { getToken } = await import("firebase/messaging");
    const token = await getToken(messagingInstance, {
      vapidKey: "BMhaCOXYLM7kOLat7BQNZtyU5noZqbgGqRLzKxbglkCY-mUveE2feitHtizwQT8dfF-hvm23hPAb9hVSo7W2K8g",
      serviceWorkerRegistration: registration
    });

    if (!token) {
      //   console.warn("No FCM token available");
      return null;
    }

    // Save token to Firestore
    if (userId) {
      await saveFcmTokenToFirestore(userId, token);
    }

    return token;
  } catch (error) {
    // console.error("Error in requestAndSaveFcmToken:", error);
    return null;
  }
};

// ✅ CHANGED: removed getToken and onMessage from exports (dynamically imported now)
// messaging exported as null — safe for any file doing: import { messaging } from './firebase'
// provider and twitterProvider already exported above — ✅ KEPT unchanged
export { db, auth, realdb };
export const messaging = null;